import { useState, useCallback, useRef } from 'react'
import type { Deal, DealStage } from '@/types'
import { checkTransition, type TransitionCheckResult } from '@/services/dealTransition.service'

interface DndState {
  draggedDealId: string | null
  draggedOverStage: DealStage | null
  isDragging: boolean
  blockedStage: DealStage | null
  blockReason: TransitionCheckResult | null
}

export function useDealsDnd(
  deals: Deal[],
  onDrop: (dealId: string, newStage: DealStage) => void,
  onBlocked?: (result: TransitionCheckResult, targetStage: DealStage) => void,
  userRole?: string | null,
) {
  const [state, setState] = useState<DndState>({
    draggedDealId: null,
    draggedOverStage: null,
    isDragging: false,
    blockedStage: null,
    blockReason: null,
  })

  const dragEnterCount = useRef<Record<string, number>>({})

  const getDraggedDeal = useCallback(
    (dealId: string | null) => {
      if (!dealId) return null
      return deals.find((d) => d.id === dealId) ?? null
    },
    [deals],
  )

  const handleDragStart = useCallback((e: React.DragEvent, dealId: string) => {
    e.dataTransfer.setData('text/plain', dealId)
    e.dataTransfer.effectAllowed = 'move'
    e.currentTarget.classList.add('dragging-v4')
    setState({ draggedDealId: dealId, draggedOverStage: null, isDragging: true, blockedStage: null, blockReason: null })
  }, [])

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    e.currentTarget.classList.remove('dragging-v4')
    dragEnterCount.current = {}
    setState({ draggedDealId: null, draggedOverStage: null, isDragging: false, blockedStage: null, blockReason: null })
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDragEnter = useCallback(
    (e: React.DragEvent, stage: DealStage) => {
      e.preventDefault()
      const key = stage
      dragEnterCount.current[key] = (dragEnterCount.current[key] || 0) + 1

      const dealId = state.draggedDealId
      const deal = getDraggedDeal(dealId)
      if (deal) {
        const check = checkTransition(deal, stage, userRole)
        const isActuallyBlocked = !check.allowed && check.code !== 'REQUIRED_FIELD_MISSING'
        if (isActuallyBlocked) {
          setState((prev) => ({ ...prev, draggedOverStage: stage, blockedStage: stage, blockReason: check }))
          onBlocked?.(check, stage)
        } else if (!check.allowed && check.code === 'REQUIRED_FIELD_MISSING') {
          setState((prev) => ({ ...prev, draggedOverStage: stage, blockedStage: null, blockReason: check }))
        } else {
          setState((prev) => ({ ...prev, draggedOverStage: stage, blockedStage: null, blockReason: null }))
        }
      } else {
        setState((prev) => ({ ...prev, draggedOverStage: stage }))
      }

      const column = e.currentTarget as HTMLElement
      column.classList.add('drag-over-v4')
    },
    [state.draggedDealId, getDraggedDeal, onBlocked, userRole],
  )

  const handleDragLeave = useCallback((e: React.DragEvent, stage: DealStage) => {
    const key = stage
    dragEnterCount.current[key] = (dragEnterCount.current[key] || 0) - 1
    if (dragEnterCount.current[key] <= 0) {
      dragEnterCount.current[key] = 0
      const column = e.currentTarget as HTMLElement
      column.classList.remove('drag-over-v4')
      setState((prev) =>
        prev.draggedOverStage === stage
          ? { ...prev, draggedOverStage: null, blockedStage: null, blockReason: null }
          : prev,
      )
    }
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent, stage: DealStage) => {
      e.preventDefault()
      const dealId = e.dataTransfer.getData('text/plain')
      const column = e.currentTarget as HTMLElement
      column.classList.remove('drag-over-v4')
      dragEnterCount.current = {}

      if (dealId) {
        const deal = getDraggedDeal(dealId)
        if (deal) {
          const check = checkTransition(deal, stage, userRole)
          const isActuallyBlocked = !check.allowed && check.code !== 'REQUIRED_FIELD_MISSING'
          if (isActuallyBlocked) {
            onBlocked?.(check, stage)
            setState({ draggedDealId: null, draggedOverStage: null, isDragging: false, blockedStage: null, blockReason: null })
            return
          }
        }
        onDrop(dealId, stage)
      }

      setState({ draggedDealId: null, draggedOverStage: null, isDragging: false, blockedStage: null, blockReason: null })
    },
    [onDrop, getDraggedDeal, onBlocked, userRole],
  )

  const isDropAllowed = useCallback(
    (stage: DealStage) => {
      const deal = getDraggedDeal(state.draggedDealId)
      if (!deal) return true
      const check = checkTransition(deal, stage, userRole)
      return check.allowed || check.code === 'REQUIRED_FIELD_MISSING'
    },
    [getDraggedDeal, state.draggedDealId, userRole],
  )

  return {
    ...state,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    isDropAllowed,
  }
}
